// /reveal?boatId=… — the magical cloud reveal (cloud-ftue boat-reveal capability,
// Slice C). A progressively-staged unveiling of a just-claimed boat:
//
//   gateways (scan + main connection, one stage) → aboard → what-we'll-watch → live
//
// reveal-gateways-onetab merged the gateway scan and the primary-gateway decision
// into ONE stage ('found'); advancing past it is gated on the pick being determined.
//
// It is READ-MOSTLY (design D2/D5): the boat runs collection + analysis and
// mirrors its whole SetupSession up as a self-healing snapshot; this page
// observes that snapshot (useBoatSetup), the device inventory (useBoatDevices),
// the gateway recommendation lane (useSourceAnalysis), and live telemetry
// (useBoatLiveData), and DERIVES the staging from them (lib/revealStaging). The
// only things it drives down are the typed, ownership-gated setup verbs
// (reanalyze / apply). The protect-the-magic invariants (tasks 7.1–7.5) live
// here: honest progress, sparse floor, offline-degrade, and NO SILENT
// COMPLETION across relay timeouts.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { api } from '../api';
import { useAuth } from '../auth';
import { RevealAboard } from '../components/reveal/RevealAboard';
import { RevealBlocking } from '../components/reveal/RevealBlocking';
import {
  RevealCredentials,
  type RevealCredentialsBundle,
} from '../components/reveal/RevealCredentials';
import { RevealGateways } from '../components/reveal/RevealGateways';
import { RevealSignalKAccess } from '../components/reveal/RevealSignalKAccess';
import { RevealStepper } from '../components/reveal/RevealStepper';
import { RevealWatch, type WatchApplyStatus } from '../components/reveal/RevealWatch';
import { getBoatPresence } from '../lib/boatPresence';
import {
  analysisKickRetryDelayMs,
  bridgePendingApproval,
  buildDecisionsPayload,
  credentialsNeeded,
  dataStageFor,
  deriveAlarmDecisions,
  isAnalysisError,
  isNoGateways,
  nextStage,
  prevStage,
  pruneRoundTrippedOverrides,
  relayFailureMessage,
  scanSettled,
  shouldPollAnalysis,
  shouldPollSetupSettle,
  stageIndex,
  type RevealAlarm,
  type RevealCandidate,
  type RevealHealthIssue,
  type RevealInput,
  type RevealStage,
  type SetupRelayVerb,
} from '../lib/revealStaging';
import {
  buildApplyDecisions,
  initialRecommendationDraft,
  type NetworkApplyDecisions,
} from '../lib/networkRecommendation';
import { cleanRevealDevices } from '../lib/revealDevices';
import { useAlarmAnalysisReady } from '../lib/useAlarmAnalysisReady';
import { useBoatDevices } from '../lib/useBoatDevices';
import { useBoatLiveData } from '../lib/useBoatLiveData';
import { useBoatSetup } from '../lib/useBoatSetup';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { useSourceAnalysis } from '../lib/useSourceAnalysis';

interface Boat {
  id: string;
  display_name: string;
  created_at: string;
  last_contacted_at: string | null;
}

type BoatLoad =
  | { kind: 'loading' }
  | { kind: 'ready'; boat: Boat }
  | { kind: 'not_found' }
  | { kind: 'error'; message: string };

// The gateway-plan apply (source_plan_push) generates the suggested alarms on the
// boat, which then mirror back via the snapshot lane. It is fire-and-forget (202,
// no command_status acks) — completion is observed as alarms appearing in the
// snapshot. If none arrive within this window we surface an explicit "couldn't
// build the alarms" state rather than spin forever (task 7.5 — no silent
// completion / no perpetual wait, the exact bug this fix targets).
//
// 30s → 75s (config-harvest B3): source_apply now chains an inline
// config-harvest pass (core source-plan.ts generateAlarmRecommendationsAfterPush)
// BEFORE alarm generation, so the boat-side work this watchdog bounds is
// harvest (~45s worst case across adapters) THEN generation, not generation
// alone — the old 30s bound would false-error a boat that's still harvesting.
const ALARM_BUILD_WATCHDOG_MS = 75_000;

// The "Start watching" arming sequence (reveal-alarm-review §5.4): unlike the
// gateway-plan apply above, both setup_record_decisions and setup_apply DO emit
// command_status beats, so each leg is observed via commandByKey rather than
// polled — but a dropped/coalesced beat is the same class of hazard, so each
// leg gets the same bounded watchdog (mirrors ALARM_BUILD_WATCHDOG_MS) rather
// than an indefinite "Starting…" spinner.
const ALARM_ARM_WATCHDOG_MS = 30_000;

// A lost/late `source_analysis_ready` WS push must not strand the determining
// beat at 404 forever (useSourceAnalysis only re-fetches on that one notify).
// While the pick is in flight, re-GET on this interval as a fallback — the
// boat-side analysis is already persisted in the dropped-push case, so the first
// poll unsticks it. Mirrors the alarm-build poll below.
const ANALYSIS_POLL_MS = 3_000;

// The setup-snapshot lane (useBoatSetup) updates only on live WS snapshot frames
// plus the mount-time bootstrap GET — it has NO periodic re-read of its own. A
// snapshot the boat mirrors in the gap between that bootstrap GET and the WS
// subscription actually attaching to the bus is lost (the fan-out has no replay),
// so the Gateways stage strands on a stale PRE-SETTLE snapshot (e.g. still
// `connection_validation`) while the boat has already advanced — the reported
// "have to refresh to move to the next stage" bug. While the scan hasn't settled
// yet, re-GET the authoritative snapshot on this interval so a missed settle-
// transition frame can't hang the scanning beat. Monotonic on emittedAt (the GET
// only applies a snapshot at least as new as the live lane's newest), so a poll
// can never regress the view; it tears down the instant the phase reaches
// device_inventory (scanSettled). Mirrors the analysis lane's fallback poll.
const SETUP_SETTLE_POLL_MS = 3_000;

// Presence (online/offline) is derived from the boat record's last_contacted_at
// (stamped by the boat's control-channel heartbeat). A one-shot mount fetch would
// freeze that input at page-load time, so a boat that's up but whose live-sample
// stream is paused reads "offline" once the initial value ages past the online
// threshold. Re-fetch /v1/boats on this interval so presence tracks the live
// heartbeat. A transient poll failure keeps the last good record (never demotes a
// loaded boat to an error screen).
const BOAT_PRESENCE_POLL_MS = 15_000;

// bridge_ftue_offer: the raw-N2K bridge's `sourceStatuses` entry is DERIVED at
// read time (core setup/index.ts `withSourceStatuses`) and is NOT itself a
// trigger for a fresh mirrored snapshot — only phase/evidence/error events ship
// one. So the connecting→connected transition an operator is actively waiting
// on (the Signal K admin approval) would otherwise sit stale until an
// unrelated snapshot happens to ship. Poll on this interval while the waiting
// sub-beat is showing, mirroring the alarm-build / setup-settle re-pulls above.
const BRIDGE_APPROVAL_POLL_MS = 3_000;

// The Devices-stage inventory push has no bus replay (same class as the
// setup-settle race), and a lost `inventory_changed` notify strands a populated
// grid just as hard as an empty one — later devices reporting in never appear.
// Poll GET /devices for the whole time the Devices stage is showing; a touch
// slower than the settle polls because this is steady-state tracking, not a
// bounded catch-up.
const DEVICES_POLL_MS = 7_000;

export function Reveal() {
  const [searchParams] = useSearchParams();
  const boatId = (searchParams.get('boatId') ?? '').trim();
  const auth = useAuth();
  const navigate = useNavigate();
  // Completing the reveal exits to the boat's dashboard (⑤): there is no
  // in-reveal 'live' stage — the dashboard IS the living boat.
  const goToBoat = useCallback(() => navigate(`/dashboard/boats/${boatId}`), [navigate, boatId]);
  // The ARMING-completion exit (alarms were actually turned on) carries a
  // one-time router-state hint so the boat landing can surface the FTUE→Alarms
  // hand-off callout (UI-5). A plain "Skip for now" exit (no alarms armed)
  // deliberately keeps goToBoat — there's nothing armed to point the owner at.
  const goToBoatArmed = useCallback(
    () => navigate(`/dashboard/boats/${boatId}`, { state: { alarmsArmed: true } }),
    [navigate, boatId]
  );

  const [boatLoad, setBoatLoad] = useState<BoatLoad>({ kind: 'loading' });
  const [userStage, setUserStage] = useState<RevealStage>('found');
  // The gateway-plan the user staged at the Gateways stage (their approved/edited
  // decisions), applied ONCE at the Devices→Alarms transition — null until they
  // confirm, and stays null if they "Skip for now" (the suggested plan is used
  // instead). Staged, not applied-on-approve: the apply is the alarm-generating
  // step and fires after the inventory stage is behind the user (the release
  // gate), reusing the source-plan relay (which now generates alarms boat-side).
  const [stagedGatewayPlan, setStagedGatewayPlan] = useState<NetworkApplyDecisions | null>(null);
  // The Alarms stage's apply/build lifecycle (④). 'building' = apply in flight,
  // no alarms yet; 'enriching' = the deterministic floor is showing but the LLM
  // enrichment is still in flight (working indicator up, forward CTA held);
  // 'idle' = settled; 'error' = nothing arrived in time. The working state is NOT
  // cleared by the first (deterministic) batch — it persists until the boat/cloud
  // `alarm_analysis_ready` signal (any terminal status) or the watchdog.
  const [alarmBuild, setAlarmBuild] = useState<WatchApplyStatus>({ state: 'idle' });
  // Settle each `alarm_analysis_ready` event once (keyed on its requestId), so a
  // stale event can't settle a later retry's build before its alarms arrive.
  const settledAlarmRequestRef = useRef<string | null>(null);
  // Fire-once latch for the Devices→Alarms transition apply.
  const gatewayApplyFiredRef = useRef(false);

  // --- reveal-alarm-review: per-alarm decisions + the "Start watching" arming
  // sequence (record-decisions → apply). Decisions are DERIVED from the
  // mirrored snapshot (deriveAlarmDecisions over alarms + session.answers,
  // design A3) every render; `decisionOverrides` is only a thin staging layer
  // for a toggle the user just made THIS session but that hasn't round-tripped
  // through the record verb yet — so a toggle can't be stranded behind a stale
  // snapshot. A persisted answer always wins once it lands: enforced (not just
  // asserted) by the pruning effect below, which drops an override the moment
  // its id shows up in session.answers, and by clearing this map outright at
  // the gateway re-flow entry point (applyGatewayPlan) — otherwise a rec that
  // gets superseded/cleared boat-side under the SAME id would silently inherit
  // the stale pre-change toggle instead of being re-asked from its default.
  const [decisionOverrides, setDecisionOverrides] = useState<Map<string, boolean>>(new Map());
  type ArmPhase = 'idle' | 'recording' | 'applying';
  const [armPhase, setArmPhase] = useState<ArmPhase>('idle');
  // The in-flight relayed command this phase is waiting to observe complete
  // (correlated via useBoatSetup's commandByKey, task 4.5's liveness lane).
  const [pendingArmCommand, setPendingArmCommand] = useState<{
    key: string;
    verb: 'record-decisions' | 'apply';
  } | null>(null);
  const armWatchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // --- config-harvest credentials sub-beat (B3) ---
  // The in-flight `setup_harvest_credentials` command this lodge is waiting to
  // observe complete, correlated via commandByKey exactly like pendingArmCommand
  // above — but tracked in its OWN state slot. A lodge can be in flight
  // alongside the record-decisions/apply arming sequence (they are independent
  // relayed commands), so sharing one slot would collide the two correlations.
  const [pendingCredentialsCommand, setPendingCredentialsCommand] = useState<{
    // null until the relay POST resolves with an idempotencyKey to correlate
    // against — the row still reads "busy" through this window (see
    // pendingGatewayId below), it just can't be matched to a beat yet.
    key: string | null;
    gatewayId: string;
  } | null>(null);
  // GatewayIds that have completed a lodge and are awaiting the re-harvest it
  // triggered — swaps that row to a "saved" state so the user isn't
  // re-prompted for the same gateway mid-flight. Cleared the moment that
  // re-harvest cycle settles (the alarmBuild.state effect further down), NOT
  // on session.needsCredentials dropping the id: core's inline harvest
  // reports a RESOLVED gateway by omitting the field entirely (it deletes the
  // in-memory entry rather than mirroring an authoritative empty array — see
  // core `SetupSessionService.setNeedsCredentials`), which is indistinguishable
  // from "no harvest has run yet" and would otherwise pin this row "Saved"
  // forever even if a wrong password re-harvests and still needs a login.
  const [lodgedGatewayIds, setLodgedGatewayIds] = useState<Set<string>>(new Set());
  // The last lodge attempt (gatewayId + bundle) — retained ONLY so the shared
  // relay-error banner's "Try again" (task 7.5, the same notice every other
  // setup verb uses) can re-fire the exact same POST; the per-row form itself
  // is the primary retry path (its username/password stay filled in).
  const lastCredentialsAttemptRef = useRef<{
    gatewayId: string;
    bundle: RevealCredentialsBundle;
  } | null>(null);

  // One-time latch for kicking source-analysis on scan-settle (reveal-gateways-onetab):
  // the gateway scan and the decision now share one stage, so kick the analysis the
  // instant discovery settles — the pick is then likelier already determined when the
  // panel's decision beat renders, shortening the "analyzing your network" wait.
  // This latch (plus the manual retry buttons) is the ONLY kick — RevealConnect's
  // mount-time auto-kick was removed after it double-fired the reanalyze. It stays
  // latched even when the relay POST fails: the settle-kick conditions can hold
  // indefinitely while the relay keeps failing (presence can read online while the
  // relay 409s), so re-arming would turn the auto-kick into a hot retry loop.
  // Recovery from a failed kick is manual (the relay-failure notice below,
  // RevealConnect's watchdog retry) — with ONE bounded exception: a kick that
  // fails with the relay's 409 boat_offline is silently re-fired on the
  // analysisKickRetryDelayMs schedule (task B5), because right after a claim
  // the boat's own WS is still in reconnect backoff and that failure
  // predictably self-heals.
  const analysisKickedRef = useRef(false);
  // The pending bounded-retry timer for a boat_offline analysis kick (task B5).
  // Held in a ref so unmount — and any fresh manual/auto kick, which supersedes
  // the schedule — can clear it; a cleared timer can never double-fire a POST.
  const analysisKickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Latest-closure bridge for that scheduled retry: the timer must fire the
  // CURRENT kick (fresh session/busy state, not the render-old closure that
  // scheduled it) and must NOT re-kick once the analysis lane has resolved by
  // other means — a redundant reanalyze is a second billed LLM run, the exact
  // double-fire reanalyzeBusy exists to prevent. Kept current by an effect
  // right after triggerAnalysis below.
  const retryAnalysisKickRef = useRef<
    (gatewaySourceId: string | undefined, attempts: number) => void
  >(() => {});
  // In-flight latch for the no-gateway rescan relay (the new setup_rescan verb).
  const [rescanBusy, setRescanBusy] = useState(false);
  // In-flight latch for the reanalyze relay, mirroring rescanBusy — without it a
  // double-clicked retry fires two setup_reanalyze POSTs (two billed LLM runs).
  const [reanalyzeBusy, setReanalyzeBusy] = useState(false);
  // A relay POST that failed (409 boat_offline, or a transport blip) — any of
  // reanalyze/rescan/record-decisions/apply. Surfaced as a dismissible retry
  // notice — a swallowed relay failure means the boat never starts the work
  // while the page waits forever (task 7.5). Cleared when the same verb later
  // relays successfully.
  const [relayError, setRelayError] = useState<{ verb: SetupRelayVerb; message: string } | null>(
    null
  );
  const [now, setNow] = useState<number>(() => Date.now());

  // --- data lanes ---
  const setup = useBoatSetup(boatId);
  const devices = useBoatDevices(boatId);
  const analysis = useSourceAnalysis(boatId);
  // The "alarm enrichment is done" signal (④), already broadcast by the cloud on
  // the boat's devices topic — consumed here to hold the working state until it
  // lands (see the alarm-build lifecycle effects below).
  const alarmReady = useAlarmAnalysisReady(boatId);
  // Live telemetry lane — retained ONLY for presence (last_sample freshness);
  // the reveal no longer renders a live telemetry stage (⑤ → dashboard).
  const liveIds = useMemo(() => (boatLoad.kind === 'ready' ? [boatLoad.boat.id] : []), [boatLoad]);
  const live = useBoatLiveData(liveIds);

  const boatName =
    boatLoad.kind === 'ready' && boatLoad.boat.display_name.trim() !== ''
      ? boatLoad.boat.display_name
      : 'your boat';
  useDocumentTitle(
    boatLoad.kind === 'ready'
      ? `Setting up ${boatName} - Neomaris`
      : 'Setting up your boat - Neomaris'
  );

  // Load the boat record (name + presence inputs). Mirrors BoatDetail.
  useEffect(() => {
    if (boatId === '') {
      setBoatLoad({ kind: 'not_found' });
      return;
    }
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const data = await api<{ boats: Boat[] }>('/v1/boats');
        if (cancelled) return;
        const match = data.boats.find((b) => b.id === boatId);
        setBoatLoad(match === undefined ? { kind: 'not_found' } : { kind: 'ready', boat: match });
      } catch (err) {
        const e = err as { status?: number; message?: string };
        if (e.status === 401) {
          await auth.refresh();
          return;
        }
        // Keep the last good record on a transient poll failure — don't demote a
        // loaded boat to the error screen just because one refresh blipped.
        if (!cancelled)
          setBoatLoad((prev) =>
            prev.kind === 'ready'
              ? prev
              : { kind: 'error', message: e.message ?? 'Failed to load boat.' }
          );
      }
    };
    void load();
    const id = setInterval(() => void load(), BOAT_PRESENCE_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [boatId, auth]);

  // 1s tick for presence + relative time.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // --- derive the staging inputs from the lanes ---
  const session = setup.state.kind === 'ready' ? setup.state.session : null;
  const candidates: RevealCandidate[] = Array.isArray(session?.candidates)
    ? (session!.candidates as RevealCandidate[])
    : [];
  // Present a CLEAN inventory (③): drop unresolved `:pending` placeholders and
  // collapse duplicate views of one physical device. Render-layer only — the
  // stored device-set (which the reinstall resolver quorums on) is untouched.
  // dataStageFor does NOT gate on deviceCount, so a cleaned (smaller) count never
  // perturbs staging; it only feeds presentational copy/counts.
  const deviceList = useMemo(
    () => (devices.state.kind === 'ready' ? cleanRevealDevices(devices.state.devices) : []),
    [devices.state]
  );
  // Memoised rather than re-derived inline, because this array's IDENTITY is a
  // dependency of the `decisions` memo below: the `[]` branch minted a fresh
  // empty array on every render, and with the 1s presence tick re-rendering this
  // page continuously that made the memo recompute forever on any boat whose
  // snapshot has no recommendations yet. The derivation is pure over `session`,
  // so keying it on the snapshot returns exactly the same value — the only thing
  // that changes is that the empty case now keeps one stable reference.
  const alarms = useMemo<RevealAlarm[]>(
    () =>
      Array.isArray(session?.recommendations) ? (session!.recommendations as RevealAlarm[]) : [],
    [session]
  );
  // Boat-computed network-health issues (FTUE↔cloud parity B2), mirrored
  // verbatim in the snapshot — see core `NetworkHealthIssue`. Pure read-only
  // render: RevealAboard/RevealWatch derive what's visible/blocking from this.
  const healthIssues: RevealHealthIssue[] = Array.isArray(session?.healthIssues)
    ? (session!.healthIssues as RevealHealthIssue[])
    : [];
  // Gateways the boat's inline config-harvest still needs a login for (B3) —
  // pure selector, authoritative on session.needsCredentials once the boat's
  // harvest has run, falling back to the discovery-time requiresCredentials
  // guess until then (lib/revealStaging credentialsNeeded).
  const credentialsNeededList = credentialsNeeded(session?.needsCredentials, candidates);
  // bridge_ftue_offer: the opted-in secured bridge's "waiting for Signal K
  // approval" status, or null when there's nothing pending — pure selector
  // over the mirrored session (lib/revealStaging bridgePendingApproval).
  const bridgePending = bridgePendingApproval(session);

  // Per-alarm accept/reject (reveal-alarm-review A2/A3): re-derived every
  // render from the mirrored snapshot so a persisted `recommendationChoice`
  // answer is always authoritative on re-entry / reload / the gateway-override
  // re-flow — `decisionOverrides` only layers a not-yet-recorded local toggle
  // on top so it doesn't visually bounce back to the default while the record
  // verb round-trips.
  const decisions = useMemo(() => {
    const base = deriveAlarmDecisions(alarms, session?.answers);
    for (const [id, accepted] of decisionOverrides) {
      if (base.has(id)) base.set(id, accepted);
    }
    return base;
  }, [alarms, session?.answers, decisionOverrides]);

  const onToggleDecision = useCallback((recommendationId: string, accepted: boolean) => {
    setDecisionOverrides((prev) => {
      const next = new Map(prev);
      next.set(recommendationId, accepted);
      return next;
    });
  }, []);

  // Prune an override the moment its decision round-trips into the mirrored
  // snapshot (a persisted `recommendationChoice` answer lands for that id) —
  // the persisted value is authoritative from there on, so the local staging
  // entry is both redundant and, if left behind, unsafe (see the
  // decisionOverrides declaration above). A no-op setState when nothing
  // round-tripped (pruneRoundTrippedOverrides returns the same reference).
  useEffect(() => {
    setDecisionOverrides((prev) => pruneRoundTrippedOverrides(prev, session?.answers));
  }, [session?.answers]);

  // The boat reaches a terminal 'complete' status on the fail-soft path (no
  // alarms generated → markComplete) or once a future activation lands. The
  // populate-only flow does not itself drive setup_apply, so 'applied' tracks the
  // session status only.
  const applied = session?.status === 'complete';

  const presence =
    boatLoad.kind === 'ready'
      ? getBoatPresence(
          boatLoad.boat.last_contacted_at,
          boatLoad.boat.created_at,
          live.lastSampleAt.get(boatLoad.boat.id),
          now
        )
      : 'connecting';

  // The analysis is actively computing while 'loading', or while 'none' with a
  // gateway found and the boat reachable (the 404='none' compute window). This holds
  // the advance gate so the stepper can't jump past the decision while it determines.
  const analysisInFlight =
    analysis.state.kind === 'loading' ||
    (analysis.state.kind === 'none' &&
      candidates.length > 0 &&
      session !== null &&
      presence !== 'offline');

  // Memoised for the same reason as `alarms` above: this object is the dependency
  // of the analysis auto-kick effect further down, so a fresh literal per render
  // re-ran that effect on every one of the 1s presence ticks. Every field is a
  // primitive read off the snapshot or a lane's state kind, so a memo keyed on
  // those recomputes exactly when a field can change — the pure consumers
  // (dataStageFor / scanSettled / isNoGateways / isAnalysisError, all called
  // inline below) still see the value they would have seen without the memo.
  // That equivalence rests entirely on the dependency list staying complete: a
  // missed field would freeze the stage ceiling mid-flow, which is why
  // exhaustive-deps being an ERROR here (not a warning) is load-bearing.
  const input: RevealInput = useMemo(
    () => ({
      hasSnapshot: session !== null,
      phase: session?.phase ?? null,
      status: session?.status ?? null,
      candidateCount: candidates.length,
      deviceCount: deviceList.length,
      analysis: analysis.state.kind,
      analysisInFlight,
      alarmCount: alarms.length,
      applied,
    }),
    [
      session,
      candidates.length,
      deviceList.length,
      analysis.state.kind,
      analysisInFlight,
      alarms.length,
      applied,
    ]
  );
  const ceiling = dataStageFor(input);

  // Fallback-poll guard: true only while the pick is genuinely in flight (the
  // determining beat). A boolean, so the lane's `none↔loading` flips don't churn
  // the interval; it tears down the instant the lane lands or the candidate/
  // session preconditions disappear. Deliberately NOT gated on `presence` — the
  // stale-offline heuristic would disable the fallback exactly when it is needed
  // (see shouldPollAnalysis).
  const analysisPollActive = shouldPollAnalysis({
    analysis: analysis.state.kind,
    settled: scanSettled(input),
    candidateCount: candidates.length,
    hasSession: session !== null,
  });

  // Fallback poll for a missed setup-snapshot frame (see SETUP_SETTLE_POLL_MS).
  // A boolean so the underlying phase churn doesn't restart the interval; it tears
  // down the moment scanSettled flips true (the pick beat then takes over, with
  // its own analysis poll).
  const setupSettlePollActive = shouldPollSetupSettle({
    setup: setup.state.kind,
    settled: scanSettled(input),
  });

  // No opening auto-advance any more (reveal-gateways-onetab): the gateway scan and
  // the primary-gateway decision are ONE stage ('found'), so the panel reveals the
  // decision beat in place as the scan settles — there is no separate stage to jump
  // to. Advancing to the device stage is a deliberate user action, gated on the
  // pick being determined (dataStageFor / the decision beat's own controls).

  // --- gateway-plan apply → alarm-build lifecycle ---
  // The plan applied at the Devices→Alarms transition: the user's staged choice,
  // or (if they skipped) the analysis's suggested plan once it is ready. Applying
  // it relays `source_plan_push`, which now generates the suggested alarms on the
  // boat; they mirror back through the snapshot lane (alarmCount > 0).
  const readyRec = analysis.state.kind === 'ready' ? analysis.state.recommendation : null;

  // A ready analysis makes a lingering "couldn't reach your boat to start the
  // network analysis" notice moot (the boat's own post-claim auto-kick can
  // deliver a result even when the client's relay raced a reconnect and
  // failed) — showing both at once reads as a contradiction.
  useEffect(() => {
    if (readyRec !== null) setRelayError((prev) => (prev?.verb === 'reanalyze' ? null : prev));
  }, [readyRec]);

  const planToApply = useMemo<NetworkApplyDecisions | null>(() => {
    if (stagedGatewayPlan !== null) return stagedGatewayPlan;
    return readyRec ? buildApplyDecisions(readyRec, initialRecommendationDraft(readyRec)) : null;
  }, [stagedGatewayPlan, readyRec]);

  const applyGatewayPlan = useCallback(
    async (plan: NetworkApplyDecisions) => {
      // Fresh build → 'building', and re-arm the settle latch so THIS build's
      // own alarm_analysis_ready is what releases it (not a stale prior event).
      settledAlarmRequestRef.current = null;
      setAlarmBuild({ state: 'building' });
      // The gateway re-flow entry point (reveal-alarm-review review fix): this
      // re-runs the boat engine, which can supersede/clear a prior
      // recommendation's persisted decision under the SAME stable rec id
      // (core recordRecommendation). Any not-yet-recorded local toggle is
      // stale the instant that happens, so wipe the whole staging layer here
      // rather than let a materially-changed alarm silently inherit a
      // pre-change choice — the re-derived decisions fall back to
      // deriveAlarmDecisions' defaults (or a freshly persisted answer) until
      // the user re-decides.
      setDecisionOverrides(new Map());
      // The build/enrich watchdog is effect-driven (keyed on alarmBuild.state)
      // so it can settle differently depending on whether alarms arrived — see
      // the watchdog effect below.
      try {
        await analysis.apply(plan);
      } catch (err) {
        const e = err as { status?: number; body?: { message?: string }; message?: string };
        const message =
          e.status === 409
            ? 'Your boat needs to be online to set up the alarms. Bring it online and try again.'
            : (e.body?.message ?? e.message ?? 'We couldn’t set up the alarms. You can try again.');
        setAlarmBuild({ state: 'error', message });
      }
    },
    [analysis]
  );

  // Fire the gateway-plan apply ONCE when the user reaches the Alarms stage and
  // alarms aren't present yet — the Devices→Alarms transition is the release gate
  // (inventory is settled, the gateway choice is staged). Waits if the plan isn't
  // ready (analysis still settling) or the boat is offline; re-runs and fires when
  // those clear. If alarms are already present (re-entry / seeded), just latch.
  useEffect(() => {
    if (gatewayApplyFiredRef.current) return;
    if (userStage !== 'watch') return;
    if (alarms.length > 0) {
      gatewayApplyFiredRef.current = true;
      return;
    }
    // Don't gate on the client `presence` heuristic — it reads stale-offline
    // when the boat is actually connected (last_contacted_at lags the live WS),
    // which would silently never fire the apply (the exact failure this whole fix
    // targets). The cloud relay is authoritative: it returns 409 boat_offline
    // when the boat truly can't be reached, which `applyGatewayPlan` surfaces as
    // an explicit, retryable error.
    if (planToApply === null) return;
    gatewayApplyFiredRef.current = true;
    void applyGatewayPlan(planToApply);
  }, [userStage, alarms.length, planToApply, applyGatewayPlan]);

  // Deterministic floor arrived while building → move to 'enriching' (④): the
  // list shows, but the working state PERSISTS (indicator up, CTA held) until the
  // LLM enrichment signals done. NOT cleared to idle here — clearing on the first
  // batch was the premature-settle that let later suggestions dribble in silently.
  useEffect(() => {
    if (alarms.length > 0 && alarmBuild.state === 'building') {
      setAlarmBuild({ state: 'enriching' });
    }
  }, [alarms.length, alarmBuild.state]);

  // The definitive settle (④): the cloud's alarm_analysis_ready signal (any
  // terminal status — ready / degraded / failed) ends the working state. Keyed on
  // requestId so each analysis run settles exactly once (a retry runs a fresh
  // analysis → new requestId → settles again). Refresh so the fully-enriched list
  // is on screen the moment we settle.
  useEffect(() => {
    if (alarmReady === null) return;
    if (settledAlarmRequestRef.current === alarmReady.requestId) return;
    settledAlarmRequestRef.current = alarmReady.requestId;
    setAlarmBuild((s) =>
      s.state === 'building' || s.state === 'enriching' ? { state: 'idle' } : s
    );
    setup.refresh();
  }, [alarmReady, setup]);

  // Watchdog (④): a lost alarm_analysis_ready (or a boat that dropped) must never
  // trap the user. While building/enriching, bound the wait — on expiry, if the
  // floor arrived ('enriching') RELEASE to idle (alarms are usable, the CTA
  // un-greys); if nothing arrived at all ('building') surface the explicit,
  // retryable error (task 7.5 — no silent perpetual wait). Effect-driven so it
  // reads the current state; restarts on the building→enriching transition.
  useEffect(() => {
    if (alarmBuild.state !== 'building' && alarmBuild.state !== 'enriching') return;
    const id = setTimeout(() => {
      setAlarmBuild((s) => {
        if (s.state === 'enriching') return { state: 'idle' };
        if (s.state === 'building') {
          return {
            state: 'error',
            message:
              'Setting up your alarms is taking longer than expected — your boat may have dropped offline. You can try again.',
          };
        }
        return s;
      });
    }, ALARM_BUILD_WATCHDOG_MS);
    return () => clearTimeout(id);
  }, [alarmBuild.state]);

  // While building OR enriching, re-pull the setup snapshot on a short interval.
  // The source_plan apply and the later LLM merge emit no command_status beats —
  // the generated/enriched alarms ride the next snapshot and a live WS push can
  // lag; polling surfaces them promptly (the effects above end the state when
  // they land / the signal fires).
  useEffect(() => {
    if (alarmBuild.state !== 'building' && alarmBuild.state !== 'enriching') return;
    const id = setInterval(() => setup.refresh(), 2_000);
    return () => clearInterval(id);
    // The RECEIVER is deliberately omitted; only the stable method is listed.
    // exhaustive-deps asks for `setup` (not `setup.refresh`) because the call is
    // a method invocation, so in the general case the receiver's identity is part
    // of what runs. Here it is not: useBoatSetup returns a fresh object literal
    // every render while `refresh` inside it is a `useCallback([])` that only
    // bumps a fetch tick. Listing `setup` would therefore tear down and re-arm
    // this interval on EVERY render — and the 1s presence tick above guarantees a
    // render every second, so a 2s period would be cleared before it ever
    // elapsed. The "correct" dependency list silently deletes the poll. Same
    // reasoning applies to the four sibling polls further down; they point here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alarmBuild.state, setup.refresh]);

  // Drop the "just lodged" markers once the re-harvest cycle they triggered
  // settles (success or error) — see lodgedGatewayIds' declaration above for
  // why this can't key off session.needsCredentials instead. A row still
  // reporting a need after this clears re-derives fresh (authoritative or
  // fallback) and re-prompts, rather than staying silently pinned "Saved".
  useEffect(() => {
    if (alarmBuild.state !== 'building' && alarmBuild.state !== 'enriching') {
      setLodgedGatewayIds((prev) => (prev.size === 0 ? prev : new Set()));
    }
  }, [alarmBuild.state]);

  // --- config-harvest credentials sub-beat (B3) ---
  // Lodge one gateway's bundle: relay the EXISTING setup_harvest_credentials
  // verb, correlate its command_status beat via commandByKey in its OWN
  // pending slot (pendingCredentialsCommand, distinct from the arming
  // sequence's), and on completion re-fire the gateway-plan apply so a rec
  // set built from the richer harvested config can supersede what's already
  // showing. Deliberately bypasses gatewayApplyFiredRef (that latch only
  // guards the ONE automatic Devices→Alarms transition fire) and reuses
  // applyGatewayPlan's own decisionOverrides wipe + watchdog/poll re-arm — a
  // re-harvest can supersede rec ids exactly like the gateway re-flow does.
  // Single-flight (one lodge at a time across all rows), mirroring armingBusy.
  const submitCredentials = useCallback(
    (gatewayId: string, bundle: RevealCredentialsBundle) => {
      if (session === null || pendingCredentialsCommand !== null) return;
      lastCredentialsAttemptRef.current = { gatewayId, bundle };
      setRelayError((prev) => (prev?.verb === 'harvest-credentials' ? null : prev));
      setPendingCredentialsCommand({ key: null, gatewayId });
      // Field-name mapping (see RevealCredentials.tsx's header comment): the
      // ONLY credential fields any harvest adapter currently reads. An empty
      // username is OMITTED (not sent as ''), so core's own `?? 'admin'`
      // default applies rather than a blank value shadowing it.
      const wireBundle: Record<string, string> = { admin_password: bundle.password };
      if (bundle.username.trim() !== '') wireBundle.admin_user = bundle.username;
      void setup
        .harvestCredentials(session.id, gatewayId, wireBundle)
        .then((key) => setPendingCredentialsCommand({ key, gatewayId }))
        .catch(() => {
          setPendingCredentialsCommand(null);
          setRelayError({
            verb: 'harvest-credentials',
            message: relayFailureMessage('harvest-credentials'),
          });
        });
    },
    [session, pendingCredentialsCommand, setup]
  );

  // Observe the pending lodge's command_status beat (mirrors the arming
  // sequence's observer effect below, against its own pending slot).
  useEffect(() => {
    if (pendingCredentialsCommand === null || pendingCredentialsCommand.key === null) return;
    const beat = setup.commandByKey.get(pendingCredentialsCommand.key);
    if (beat === undefined) return;
    const { gatewayId } = pendingCredentialsCommand;
    if (beat.phase === 'complete') {
      setPendingCredentialsCommand(null);
      setLodgedGatewayIds((prev) => {
        const next = new Set(prev);
        next.add(gatewayId);
        return next;
      });
      if (planToApply !== null) void applyGatewayPlan(planToApply);
    } else if (beat.phase === 'rejected') {
      setPendingCredentialsCommand(null);
      setRelayError({
        verb: 'harvest-credentials',
        message: relayFailureMessage('harvest-credentials'),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setup.lastCommand, pendingCredentialsCommand, planToApply, applyGatewayPlan]);

  // Bounded watchdog for the lodge: a 202-relayed command whose terminal beat
  // never arrives (boat acks then drops, beat lost/coalesced) would otherwise
  // strand the row on "Saving…" forever — the single-flight guard then blocks
  // any retry (task 7.5: no perpetual wait). Keyed on the pending slot object,
  // so the relay POST resolving (key null → real key) restarts the timer and
  // the observer effect settling it (slot → null) cancels via cleanup.
  useEffect(() => {
    if (pendingCredentialsCommand === null) return;
    const id = setTimeout(() => {
      setPendingCredentialsCommand(null);
      setRelayError({
        verb: 'harvest-credentials',
        message: relayFailureMessage('harvest-credentials'),
      });
    }, ALARM_ARM_WATCHDOG_MS);
    return () => clearTimeout(id);
  }, [pendingCredentialsCommand]);

  // --- "Start watching" arming sequence (reveal-alarm-review §5.4) ---
  // record-decisions → (observe complete) → apply → (observe complete) → live.
  // Chained by watching each relayed command's command_status beat via
  // commandByKey, rather than the alarm-build's snapshot-poll pattern above,
  // because both setup_record_decisions and setup_apply DO emit those beats.
  const clearArmWatchTimer = useCallback(() => {
    if (armWatchTimerRef.current !== null) {
      clearTimeout(armWatchTimerRef.current);
      armWatchTimerRef.current = null;
    }
  }, []);
  useEffect(() => () => clearArmWatchTimer(), [clearArmWatchTimer]);

  const armingBusy = armPhase !== 'idle';

  // A leg (record-decisions or apply) failed to relay, timed out, or was
  // rejected boat-side — surface the SAME retry-banner pattern the other
  // setup verbs use (task 7.5: no silent swallow) and drop back to idle so
  // "Start watching" can be pressed again.
  const failArming = useCallback(
    (verb: 'record-decisions' | 'apply') => {
      clearArmWatchTimer();
      setPendingArmCommand(null);
      setArmPhase('idle');
      setRelayError({ verb, message: relayFailureMessage(verb) });
    },
    [clearArmWatchTimer]
  );

  // Fires ONLY after record-decisions has observably completed (never races
  // apply reading the decisions before they're written).
  const fireApply = useCallback(async () => {
    if (session === null) {
      failArming('apply');
      return;
    }
    setArmPhase('applying');
    clearArmWatchTimer();
    armWatchTimerRef.current = setTimeout(() => failArming('apply'), ALARM_ARM_WATCHDOG_MS);
    try {
      const key = await setup.apply(session.id);
      setPendingArmCommand({ key, verb: 'apply' });
    } catch {
      failArming('apply');
    }
  }, [session, setup, clearArmWatchTimer, failArming]);

  // The forward CTA on the Alarms stage. With no alarms, this is the
  // unchanged navigational skip (no verb fired, task 2.3/§5.4.7). With alarms,
  // it records the FULL decision set (every alarms[].id — completeness by
  // construction) then, once that's observed complete, applies it.
  const startWatching = useCallback(() => {
    if (alarms.length === 0) {
      goToBoat();
      return;
    }
    if (session === null || armingBusy) return;
    setRelayError((prev) =>
      prev !== null && (prev.verb === 'record-decisions' || prev.verb === 'apply') ? null : prev
    );
    setArmPhase('recording');
    clearArmWatchTimer();
    armWatchTimerRef.current = setTimeout(
      () => failArming('record-decisions'),
      ALARM_ARM_WATCHDOG_MS
    );
    const payload = buildDecisionsPayload(decisions);
    void setup
      .recordDecisions(session.id, payload)
      .then((key) => setPendingArmCommand({ key, verb: 'record-decisions' }))
      .catch(() => failArming('record-decisions'));
  }, [
    alarms.length,
    session,
    armingBusy,
    decisions,
    setup,
    clearArmWatchTimer,
    failArming,
    goToBoat,
  ]);

  // Observe the pending command's command_status beat (re-runs whenever a new
  // beat arrives, via setup.lastCommand — commandByKey is mutated in place so
  // its own identity never changes across renders).
  useEffect(() => {
    if (pendingArmCommand === null) return;
    const beat = setup.commandByKey.get(pendingArmCommand.key);
    if (beat === undefined) return;
    if (beat.phase === 'complete') {
      clearArmWatchTimer();
      if (pendingArmCommand.verb === 'record-decisions') {
        setPendingArmCommand(null);
        void fireApply();
      } else {
        setPendingArmCommand(null);
        setArmPhase('idle');
        goToBoatArmed();
      }
    } else if (beat.phase === 'rejected') {
      failArming(pendingArmCommand.verb);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setup.lastCommand, pendingArmCommand, clearArmWatchTimer, fireApply, failArming]);

  // `session.status === 'complete'` is the SECONDARY completion authority for
  // the apply leg (contract §5.4.5): setup_apply marks the session complete
  // when cloud-connected, and that snapshot flag can land (or already be
  // stored) even if this specific command_status beat is dropped/coalesced —
  // the exact class of hazard the alarm-build watchdog above guards against.
  // Without this a lost apply beat would false-error the arming sequence at
  // the watchdog even though the boat actually finished.
  useEffect(() => {
    if (armPhase === 'applying' && applied) {
      clearArmWatchTimer();
      setPendingArmCommand(null);
      setArmPhase('idle');
      goToBoatArmed();
    }
  }, [armPhase, applied, clearArmWatchTimer, goToBoatArmed]);

  // Kick a re-analysis from retained candidates (connect stage / blocking retry).
  // Latches analysisKickedRef so the scan-settle auto-kick can never follow a
  // manual kick (the double-fire the latch exists to prevent). `failedAttempts`
  // is internal to the bounded boat_offline retry (task B5) — every manual /
  // auto-kick caller starts a fresh schedule at 0.
  const clearAnalysisKickTimer = useCallback(() => {
    if (analysisKickTimerRef.current !== null) {
      clearTimeout(analysisKickTimerRef.current);
      analysisKickTimerRef.current = null;
    }
  }, []);
  const triggerAnalysis = useCallback(
    (gatewaySourceId?: string, failedAttempts = 0) => {
      if (session === null || reanalyzeBusy) return;
      // Any fresh kick supersedes a pending scheduled retry — never two lanes.
      clearAnalysisKickTimer();
      analysisKickedRef.current = true;
      setReanalyzeBusy(true);
      void api(`/v1/boats/${boatId}/setup/reanalyze`, {
        method: 'POST',
        body: {
          sessionId: session.id,
          ...(gatewaySourceId !== undefined ? { gatewaySourceId } : {}),
        },
      })
        .then(() => {
          // A later successful relay clears a stale reanalyze failure notice.
          setRelayError((prev) => (prev?.verb === 'reanalyze' ? null : prev));
        })
        .catch((error: unknown) => {
          // The relay never reached the boat (409 boat_offline / transport
          // blip) — the analysis will not start, so a swallow here would leave
          // the determining beat waiting forever. Right after a claim the
          // boat's own WS is still in reconnect backoff, so the 409
          // boat_offline specifically is expected AND self-healing: re-fire it
          // silently on the bounded analysisKickRetryDelayMs schedule (task
          // B5) before troubling the user. Everything else — and offline past
          // the retry budget — surfaces the retryable notice; the kick latch
          // deliberately stays set (see its declaration) so recovery beyond
          // that bounded schedule is user-driven, never an auto loop against
          // a failing relay.
          const retryDelayMs = analysisKickRetryDelayMs(error, failedAttempts + 1);
          if (retryDelayMs !== null) {
            analysisKickTimerRef.current = setTimeout(() => {
              analysisKickTimerRef.current = null;
              retryAnalysisKickRef.current(gatewaySourceId, failedAttempts + 1);
            }, retryDelayMs);
            return;
          }
          setRelayError({ verb: 'reanalyze', message: relayFailureMessage('reanalyze') });
        })
        .finally(() => {
          setReanalyzeBusy(false);
        });
      analysis.refresh();
    },
    [boatId, session, reanalyzeBusy, analysis, clearAnalysisKickTimer]
  );

  // Keep the scheduled-retry bridge (retryAnalysisKickRef, declared with the
  // timer ref above) pointing at the current kick + analysis-lane state.
  useEffect(() => {
    retryAnalysisKickRef.current = (gatewaySourceId, attempts) => {
      if (analysis.state.kind === 'ready') return;
      triggerAnalysis(gatewaySourceId, attempts);
    };
  }, [analysis.state.kind, triggerAnalysis]);

  // Unmount-only cleanup: a retry scheduled on this page must die with it.
  useEffect(() => clearAnalysisKickTimer, [clearAnalysisKickTimer]);

  const canTriggerAnalysis = session !== null && presence !== 'offline';

  // No-gateway rescan (reveal-gateways-onetab D4): relay the scan-restart verb and
  // let the snapshot lane stream the new scan back into the scanning beat. A busy
  // latch disables the button during the relay; a relay failure surfaces the same
  // retryable notice as the reanalyze (and `finally` clears busy for the retry).
  const doRescan = useCallback(async () => {
    if (session === null || rescanBusy) return;
    setRescanBusy(true);
    try {
      await setup.rescan(session.id);
      setRelayError((prev) => (prev?.verb === 'rescan' ? null : prev));
    } catch {
      setRelayError({ verb: 'rescan', message: relayFailureMessage('rescan') });
    } finally {
      setRescanBusy(false);
    }
  }, [session, rescanBusy, setup]);

  // Kick source-analysis as soon as discovery settles with something found, so it
  // is in flight before the user reaches the (now-first) decision stage (task
  // 2.8). Fire-once via analysisKickedRef — this is the ONLY auto-kick
  // (RevealConnect's mount-time kick was removed after it double-fired the
  // reanalyze); the latch never re-arms, even on relay failure (see its
  // declaration), so a failing relay can't turn this effect into a retry loop.
  useEffect(() => {
    if (analysisKickedRef.current) return;
    if (
      scanSettled(input) &&
      candidates.length > 0 &&
      analysis.state.kind === 'none' &&
      canTriggerAnalysis
    ) {
      triggerAnalysis();
    }
  }, [input, candidates.length, analysis.state.kind, canTriggerAnalysis, triggerAnalysis]);

  // Fallback poll for a dropped/coalesced `source_analysis_ready` push: while the
  // pick is in flight, nudge the same GET the WS notify drives (analysis.refresh →
  // tick), so a missed push can't strand the determining gate. Stops the moment
  // the lane resolves, the boat drops offline, or candidates/session disappear
  // (analysisPollActive flips false → interval torn down). Mirrors the alarm-build
  // poll above. analysis.refresh is a stable useCallback([]) so it never churns.
  useEffect(() => {
    if (!analysisPollActive) return;
    const id = setInterval(() => analysis.refresh(), ANALYSIS_POLL_MS);
    return () => clearInterval(id);
    // Receiver omitted on purpose — see the alarm-build poll above for the full
    // reasoning: depending on `analysis` (a fresh object literal per render)
    // instead of its stable `refresh` re-arms this interval every second and the
    // 3s poll never fires.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisPollActive, analysis.refresh]);

  // Re-read the setup snapshot while the scan is still settling, so a snapshot
  // frame missed during the subscribe/publish race can't strand the Gateways
  // stage on the scanning beat until a manual refresh (setup.refresh is a stable
  // useCallback([]) so it never churns the interval).
  useEffect(() => {
    if (!setupSettlePollActive) return;
    const id = setInterval(() => setup.refresh(), SETUP_SETTLE_POLL_MS);
    return () => clearInterval(id);
    // Receiver omitted on purpose — see the alarm-build poll above. This is the
    // poll that unsticks the "have to refresh to move to the next stage" bug, so
    // letting it be re-armed once a second would reintroduce exactly that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setupSettlePollActive, setup.refresh]);

  // bridge_ftue_offer: poll while the opted-in bridge is waiting on Signal K
  // approval (see BRIDGE_APPROVAL_POLL_MS) — the connecting→connected flip
  // isn't itself a snapshot-shipping event boat-side, so without this the
  // waiting sub-beat could sit stale until an unrelated snapshot ships.
  const bridgeApprovalPending = bridgePending !== null;
  useEffect(() => {
    if (!bridgeApprovalPending) return;
    const id = setInterval(() => setup.refresh(), BRIDGE_APPROVAL_POLL_MS);
    return () => clearInterval(id);
    // Receiver omitted on purpose — see the alarm-build poll above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bridgeApprovalPending, setup.refresh]);

  // The device inventory (useBoatDevices) fills from a boat-side push with no bus
  // replay — an `inventory_changed` notification lost in the subscribe gap (or
  // dropped by the fan-out) strands the grid on whatever the last GET returned:
  // the empty "still reading" floor, or a populated grid that silently stops
  // tracking late-reporting devices. While the Devices stage is showing, re-GET
  // on this interval so the grid fills and keeps tracking without a manual
  // reload. Mirrors the setup-settle poll; devices.refresh is a stable
  // useCallback([]).
  useEffect(() => {
    if (userStage !== 'aboard') return;
    const id = setInterval(() => devices.refresh(), DEVICES_POLL_MS);
    return () => clearInterval(id);
    // Receiver omitted on purpose — see the alarm-build poll above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userStage, devices.refresh]);

  // --- top-level load gates ---
  if (boatLoad.kind === 'loading') {
    return (
      <section className="dashboard reveal-page">
        <p className="dashboard-muted">Loading…</p>
      </section>
    );
  }
  if (boatLoad.kind === 'not_found') {
    return (
      <section className="dashboard reveal-page">
        <header className="dashboard-head">
          <h2>Boat not found</h2>
          <p className="dashboard-sub">
            This boat isn’t paired with your account, or it was deleted.
          </p>
        </header>
        <p>
          <Link to="/dashboard" className="boat-detail-link">
            Back to your boats
          </Link>
        </p>
      </section>
    );
  }
  if (boatLoad.kind === 'error') {
    return (
      <section className="dashboard reveal-page">
        <ul className="form-errors" role="alert">
          <li>{boatLoad.message}</li>
        </ul>
        <p>
          <Link to="/dashboard" className="boat-detail-link">
            Back to your boats
          </Link>
        </p>
      </section>
    );
  }
  if (setup.state.kind === 'error') {
    return (
      <section className="dashboard reveal-page">
        <ul className="form-errors" role="alert">
          <li>{setup.state.message}</li>
        </ul>
        <p>
          <Link to={`/dashboard/boats/${boatId}`} className="boat-detail-link">
            Go to your boat
          </Link>
        </p>
      </section>
    );
  }

  // Offline-degrade (task 7.4): if we lost the live setup lane but still hold a
  // snapshot, keep rendering and show a quiet reconnecting note — never an error.
  const reconnecting = setup.connection === 'closed' && session !== null;

  // --- which content does the current stage resolve to? ---
  // No-gateway HARD STOP (reveal-gateways-onetab D3): a settled scan that found NO
  // gateway can't observe the boat — block with the rescan-only requirement screen.
  // Keyed on isNoGateways (candidateCount === 0); a gateways-present-but-no-devices
  // boat is NOT blocked (it flows through to the device stage's friendly floor).
  // candidateCount comes from the mirrored snapshot, so this never flashes before a
  // settled snapshot arrives (isNoGateways requires scanSettled).
  const blockedNoGateways =
    (userStage === 'found' || userStage === 'aboard') && isNoGateways(input);
  // A hard analysis failure on the decision beat routes here (not a stuck button);
  // the decision now lives in the merged 'found' stage. Gated on `scanSettled` so a
  // transient analysis-lane error during the live scan (or on initial load, before
  // a snapshot) can't pre-empt the scanning beat — the decision beat only renders
  // once settled, and so must the error screen that backs it. No-gateway wins if both.
  const blockedAnalysis =
    userStage === 'found' && scanSettled(input) && !blockedNoGateways && isAnalysisError(input);

  let stageContent: JSX.Element;
  if (blockedNoGateways) {
    stageContent = (
      <RevealBlocking
        kind="no_gateways"
        onRetry={() => void doRescan()}
        retryLabel="Rescan"
        retryDisabled={presence === 'offline' || rescanBusy}
        busy={rescanBusy}
        detail={
          presence === 'offline'
            ? 'Your boat looks offline right now — bring it online first, then rescan.'
            : undefined
        }
      />
    );
  } else if (blockedAnalysis) {
    stageContent = (
      <RevealBlocking
        kind="analysis_error"
        detail={
          presence === 'offline'
            ? 'Your boat looks offline right now — bring it online first.'
            : undefined
        }
        footnote={
          deviceList.length > 0
            ? `We still found ${deviceList.length} ${
                deviceList.length === 1 ? 'device' : 'devices'
              } aboard — they're safe while we retry.`
            : undefined
        }
        onRetry={() => {
          analysis.refresh();
          triggerAnalysis();
        }}
        retryLabel="Try again"
        retryDisabled={presence === 'offline'}
        onContinue={() => setUserStage('aboard')}
        continueLabel="Skip for now"
      />
    );
  } else {
    switch (userStage) {
      case 'found':
        // The merged Gateways stage: scan beat → determine the primary → the pick,
        // all in one panel. STAGE the chosen plan (onApply) — the alarm-generating
        // apply fires later, at the Devices→Alarms transition (the release gate).
        stageContent = (
          <RevealGateways
            candidates={candidates}
            candidateCount={candidates.length}
            settled={scanSettled(input)}
            hasSnapshot={session !== null}
            connecting={presence === 'connecting'}
            phase={session?.phase ?? null}
            analysis={analysis.state}
            deviceCount={deviceList.length}
            onApply={async (decisions) => {
              setStagedGatewayPlan(decisions);
            }}
            onReevaluate={analysis.reevaluate}
            reevaluating={analysis.reevaluating}
            onTriggerAnalysis={() => triggerAnalysis()}
            canTriggerAnalysis={canTriggerAnalysis}
            onContinue={() => setUserStage('aboard')}
          />
        );
        break;
      case 'aboard':
        stageContent = (
          <RevealAboard
            devices={deviceList}
            deviceCount={deviceList.length}
            candidateCount={candidates.length}
            loading={devices.state.kind === 'loading'}
            error={devices.state.kind === 'error' ? devices.state.message : null}
            healthIssues={healthIssues}
          />
        );
        break;
      case 'watch':
        stageContent = (
          <>
            <RevealSignalKAccess pending={bridgePending} />
            <RevealCredentials
              needed={credentialsNeededList}
              pendingGatewayId={pendingCredentialsCommand?.gatewayId ?? null}
              lodgedGatewayIds={lodgedGatewayIds}
              onSubmit={submitCredentials}
            />
            <RevealWatch
              alarms={alarms}
              status={alarmBuild}
              decisions={decisions}
              onToggle={onToggleDecision}
              onStartWatching={startWatching}
              arming={armingBusy}
              armingDisabled={presence === 'offline'}
              onRetry={() => {
                if (planToApply !== null) void applyGatewayPlan(planToApply);
              }}
              healthIssues={healthIssues}
            />
          </>
        );
        break;
    }
  }

  // A global footer nav only on the device stage ('aboard'), which has no forward
  // action of its own. The merged Gateways stage ('found') owns its forward via the
  // decision beat (Apply / Skip — gated until the pick lands), and watch / live own
  // their CTAs.
  const showFooterContinue = !blockedNoGateways && !blockedAnalysis && userStage === 'aboard';
  const back = prevStage(userStage);
  const fwd = nextStage(userStage);

  // The relay notice's retry mirrors the sibling retry affordances
  // (RevealBlocking): busy while its verb's relay is in flight, disabled while
  // the boat reads offline — the copy already says to bring it online first.
  const relayRetryBusy =
    relayError?.verb === 'rescan'
      ? rescanBusy
      : relayError?.verb === 'record-decisions' || relayError?.verb === 'apply'
        ? armingBusy
        : relayError?.verb === 'harvest-credentials'
          ? pendingCredentialsCommand !== null
          : reanalyzeBusy;

  return (
    <section className="dashboard reveal-page boat-reveal" data-testid="reveal">
      <header className="reveal-head">
        <div className="boat-name-row">
          <span
            className={`boat-status-dot boat-status-dot--${presence}`}
            aria-hidden="true"
            title={presence}
          />
          <h2 style={{ margin: 0 }}>{boatName}</h2>
        </div>
        <p className="dashboard-sub">
          {stageHeadline(userStage)}
          {reconnecting ? <span className="reveal-reconnecting"> · reconnecting…</span> : null}
        </p>
      </header>

      <RevealStepper
        current={userStage}
        ceiling={ceiling}
        onJump={(s) => {
          // Only allow jumping to a settled stage (no skipping ahead of data).
          if (stageIndex(s) <= Math.max(stageIndex(ceiling), stageIndex(userStage))) {
            setUserStage(s);
          }
        }}
      />

      {relayError !== null ? (
        <div className="reveal-relay-error" role="alert" data-testid="reveal-relay-error">
          <p className="reveal-relay-error__message">{relayError.message}</p>
          <div className="reveal-relay-error__actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                if (relayError.verb === 'rescan') void doRescan();
                else if (relayError.verb === 'record-decisions' || relayError.verb === 'apply')
                  startWatching();
                else if (relayError.verb === 'harvest-credentials') {
                  // Re-fire the exact same lodge — the row's own form is the
                  // primary retry path, this is the shared-banner fallback.
                  const attempt = lastCredentialsAttemptRef.current;
                  if (attempt !== null) submitCredentials(attempt.gatewayId, attempt.bundle);
                } else triggerAnalysis();
              }}
              disabled={relayRetryBusy || presence === 'offline'}
              data-testid="reveal-relay-error-retry"
            >
              {relayRetryBusy ? 'Trying…' : 'Try again'}
            </button>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => setRelayError(null)}
              data-testid="reveal-relay-error-dismiss"
            >
              Dismiss
            </button>
          </div>
        </div>
      ) : null}

      <div className="reveal-body">{stageContent}</div>

      {showFooterContinue ? (
        <footer className="reveal-foot">
          {back !== null ? (
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => setUserStage(back)}
              data-testid="reveal-back"
            >
              Back
            </button>
          ) : (
            <span />
          )}
          {fwd !== null ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setUserStage(fwd)}
              data-testid="reveal-continue"
            >
              Continue
            </button>
          ) : null}
        </footer>
      ) : null}
    </section>
  );
}

function stageHeadline(stage: RevealStage): string {
  switch (stage) {
    case 'found':
      return 'Your gateways and main connection';
    case 'aboard':
      return 'Here’s what we found aboard';
    case 'watch':
      return 'What we’ll keep an eye on';
  }
}
